import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, LockOpen, TriangleAlert } from 'lucide-react'

import { payrollApi } from '@/api/endpoints'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, StatCard } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import {
  formatAmount,
  formatMinutes,
  formatMonthLabel,
  formatRate,
  previousMonth,
} from '@/lib/billing'
import { plural } from '@/lib/format'

export function PayrollReportPanel() {
  const { notifySuccess, notifyError } = useToast()
  const queryClient = useQueryClient()

  // Domyślnie poprzedni miesiąc — ten, który się właśnie rozlicza.
  const [okres, setOkres] = useState(() => previousMonth())

  const reportQuery = useQuery({
    queryKey: ['payroll', 'report', okres.year, okres.month],
    queryFn: () => payrollApi.report(okres.year, okres.month),
    placeholderData: keepPreviousData,
  })

  const odblokuj = useMutation({
    mutationFn: (teacherId: string) => payrollApi.reopenMonth(okres.year, okres.month, teacherId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
      void queryClient.invalidateQueries({ queryKey: ['attendance'] })
      notifySuccess('Miesiąc odblokowany — nauczyciel może poprawić wpisy.')
    },
    onError: (error: Error) => notifyError(error.message),
  })

  const przesun = (o: number) => {
    setOkres((poprzedni) => {
      const data = new Date(poprzedni.year, poprzedni.month - 1 + o, 1)
      return { year: data.getFullYear(), month: data.getMonth() + 1 }
    })
  }

  const dane = reportQuery.data

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-2">
            <IconButton
              label="Poprzedni miesiąc"
              icon={<ChevronLeft size={18} />}
              onClick={() => przesun(-1)}
            />
            <p className="min-w-44 text-center font-serif text-lg text-on-surface">
              {formatMonthLabel(okres)}
            </p>
            <IconButton
              label="Następny miesiąc"
              icon={<ChevronRight size={18} />}
              onClick={() => przesun(1)}
            />
          </div>
          {dane && (
            <p className="text-sm text-on-surface-variant">
              Zamkniętych: <strong className="text-on-surface">{dane.closed_count}</strong> z{' '}
              {dane.rows.length}
            </p>
          )}
        </div>
      </Card>

      {dane && dane.pending_count > 0 && (
        <Alert tone="warning" title="Nie wszyscy rozliczyli miesiąc">
          {plural(
            dane.pending_count,
            'Jeden nauczyciel ma zajęcia bez wpisanej obecności.',
            `${dane.pending_count} nauczycieli ma zajęcia bez wpisanej obecności.`,
            `${dane.pending_count} nauczycieli ma zajęcia bez wpisanej obecności.`,
          )}{' '}
          Do czasu uzupełnienia wpisów nie mogą zamknąć miesiąca, a kwoty poniżej są wstępne.
        </Alert>
      )}

      {dane && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Do wypłaty łącznie"
            value={formatAmount(dane.total_amount)}
            icon={<span className="font-serif text-lg">zł</span>}
            tone="success"
          />
          <StatCard
            label="Miesiąc zamknięty przez"
            value={`${dane.closed_count} / ${dane.rows.length}`}
            icon={<span className="font-serif text-lg">✓</span>}
          />
          <StatCard
            label="Z zaległymi wpisami"
            value={String(dane.pending_count)}
            icon={<TriangleAlert size={20} />}
            tone={dane.pending_count > 0 ? 'warning' : 'default'}
          />
        </div>
      )}

      <Card>
        {reportQuery.isPending ? (
          <SkeletonRows rows={4} />
        ) : (
          <DataTable caption={`Raport do wypłat — ${formatMonthLabel(okres)}`}>
            <TableHead
              columns={[
                'Nauczyciel',
                'Status',
                'Lekcje płatne',
                'Czas',
                'Stawka',
                'Kwota',
                { label: 'Akcje', srOnly: true },
              ]}
            />
            <TableBody>
              {(dane?.rows ?? []).length === 0 ? (
                <EmptyRow colSpan={7} message="W tym miesiącu nie było zajęć." />
              ) : (
                (dane?.rows ?? []).map((row) => (
                  <TableRow key={row.teacher.id}>
                    <TableCell>{row.teacher.full_name}</TableCell>
                    <TableCell>
                      {row.is_closed ? (
                        <Badge tone="success">Zamknięty</Badge>
                      ) : row.pending > 0 ? (
                        <Badge tone="warning">{row.pending} bez wpisu</Badge>
                      ) : (
                        <Badge tone="neutral">Otwarty</Badge>
                      )}
                    </TableCell>
                    <TableCell>{row.lessons_payable}</TableCell>
                    <TableCell>{formatMinutes(row.payable_minutes)}</TableCell>
                    <TableCell>{formatRate(row.hourly_rate)}</TableCell>
                    <TableCell>
                      <strong>{formatAmount(row.amount)}</strong>
                    </TableCell>
                    <TableCell>
                      {row.is_closed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<LockOpen size={14} />}
                          isLoading={odblokuj.isPending}
                          onClick={() => odblokuj.mutate(row.teacher.id)}
                        >
                          Odblokuj
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </DataTable>
        )}
      </Card>
    </div>
  )
}
